import { gql, useQuery } from '@apollo/client'
import { useCallback, useEffect } from 'react'
import type { ViewCountKeyEnum } from '@ztechapps/common.contract'
import { useMutation } from '../useMutation'

const viewCountQuery = gql`
  query viewCount($input: GetViewCountArgs!) {
    viewCount(input: $input)
  }
`

const incrementViewCountMutation = gql`
  mutation incrementViewCount($input: IncrementViewCountArgs!) {
    incrementViewCount(input: $input) {
      _id
      key
      value
      count
    }
  }
`

export type UseViewCountArgs = {
  key: ViewCountKeyEnum
  value: string
}

export type UseViewCountResult = {
  viewCount: number
  increment: () => Promise<void>
  loading: boolean
  error: Error | undefined
}

/**
 * Generic hook for managing view counts of any entity.
 *
 * **IMPORTANT: Auto-increment Behavior**
 * This hook automatically increments the view count when the component mounts
 * AND whenever the `value` or `key` parameters change. This means:
 * - Each component mount = +1 view count
 * - Each time the entity ID changes = +1 view count
 * - Navigation away and back = +1 view count (new mount)
 * - Hot module reload in development = +1 view count (re-mount)
 *
 * Be aware of this behavior when using the hook to avoid unintended increments.
 * If you need to track views without auto-increment, use the ViewCount GraphQL
 * query and mutation directly instead.
 *
 * @param key - The entity type (from ViewCountKeyEnum)
 * @param value - The entity ID
 * @returns viewCount (current count), increment (manual increment function), loading, error
 *
 * @example
 * const { viewCount, increment, loading } = useViewCount({
 *   key: ViewCountKeyEnum.Order,
 *   value: orderId
 * })
 */
export function useViewCount({ key, value }: UseViewCountArgs): UseViewCountResult {
  // Query for getting the current view count
  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery(viewCountQuery, {
    variables: { input: { key, value } },
    skip: !value,
    fetchPolicy: 'cache-and-network',
  })

  // Mutation for incrementing the view count
  const [incrementMutation, , { loading: mutationLoading, error: mutationError }] = useMutation({
    query: incrementViewCountMutation,
  })

  // Extract the view count from the query result
  const viewCount = data?.viewCount ?? 0

  // Function to manually increment the view count
  const increment = useCallback(async () => {
    if (!value) return
    await incrementMutation({ input: { key, value } })
  }, [value, key, incrementMutation])

  // Auto-increment on mount and when key/value changes
  useEffect(() => {
    if (!value) return
    incrementMutation({ input: { key, value } })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit incrementMutation to prevent infinite loop
  }, [value, key])

  return {
    viewCount,
    increment,
    loading: queryLoading || mutationLoading,
    error: (queryError as Error) || (mutationError as Error),
  }
}
